import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { invitationService } from '../services/invitation';
import { Invitation } from '../types';

const Invitations: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [expiresHours, setExpiresHours] = useState<number>(24);

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const data = await invitationService.list();
      setInvitations(data);
    } catch (error) {
      console.error('Error loading invitations:', error);
    }
  };

  const handleCreate = async () => {
    setLoading(true);
    try {
      await invitationService.create({
        email: email || undefined,
        expires_hours: expiresHours
      });
      setEmail('');
      await loadInvitations();
      alert('Invitation created successfully!');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Error creating invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this invitation?')) {
      try {
        await invitationService.delete(id);
        await loadInvitations();
      } catch (error: any) {
        alert(error.response?.data?.detail || 'Error deleting invitation');
      }
    }
  };

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    alert('Invitation code copied to clipboard!');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-indigo-600 hover:text-indigo-800"
            >
              ← Back
            </button>
            <h1 className="text-2xl font-bold text-indigo-600">Manage Invitations</h1>
          </div>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
          >
            Logout
          </button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto p-4">
        {/* Create new invitation */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Create New Invitation</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Email (optional)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Leave empty for any email"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div>
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Expires in (hours)
              </label>
              <input
                type="number"
                value={expiresHours}
                onChange={(e) => setExpiresHours(parseInt(e.target.value))}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <button
              onClick={handleCreate}
              disabled={loading}
              className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {loading ? 'Creating...' : 'Create Invitation'}
            </button>
          </div>
        </div>

        {/* Invitations list */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">Your Invitations</h2>
          
          <div className="space-y-3">
            {invitations.map((inv) => (
              <div
                key={inv.id}
                className={`p-4 border rounded-lg ${
                  inv.is_used ? 'bg-gray-50 opacity-60' : 'bg-white'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <code className="bg-gray-100 px-3 py-1 rounded font-mono text-sm">
                        {inv.code}
                      </code>
                      <button
                        onClick={() => copyToClipboard(inv.code)}
                        className="text-indigo-600 hover:text-indigo-800 text-sm"
                      >
                        📋 Copy
                      </button>
                    </div>
                    
                    {inv.email && (
                      <p className="text-sm text-gray-600">For: {inv.email}</p>
                    )}
                    
                    <p className="text-sm text-gray-600">
                      Created: {new Date(inv.created_at).toLocaleString()}
                    </p>
                    
                    {inv.expires_at && (
                      <p className="text-sm text-gray-600">
                        Expires: {new Date(inv.expires_at).toLocaleString()}
                      </p>
                    )}
                    
                    {inv.is_used && (
                      <p className="text-sm text-green-600 font-bold">
                        ✓ Used on {new Date(inv.used_at!).toLocaleString()}
                      </p>
                    )}
                  </div>

                  {!inv.is_used && (
                    <button
                      onClick={() => handleDelete(inv.id)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Delete
                    </button>
                  )}
                </div>
              </div>
            ))}

            {invitations.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                No invitations yet. Create one above!
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Invitations;
